// The approval queue — the screen where a stranger becomes a customer.
//
// ── Two rules this screen is built around ───────────────────────────────────
//
// 1. A FLAG NEVER HIDES A ROW. Decisions D10 and D11 in the design doc: a real
//    prospect writing from a personal address is still a real prospect, and a
//    second request from a domain that already has a customer is usually a
//    colleague, not an attack. The gateway computes the flags; this screen tints
//    the row and shows them. Omar decides. A queue that filtered would lose a
//    customer silently, which is the one failure nobody ever finds out about.
//
// 2. THE APPROVE BUTTON EMAILS A REAL PERSON. Everything about it is shaped by
//    that: a synchronous latch rather than a `disabled` prop (see
//    `useSubmitLatch`), a dialog that will not close mid-flight, no automatic
//    retry anywhere in the client, and a failure state that reads the row back
//    from the server before telling anybody what happened.

import { useState } from "react";
import {
  ApiError,
  type ApproveResult,
  type ConsoleApi,
  type RegistrationRequest,
  type RequestList,
} from "./api.js";
import {
  ErrorState,
  fmtDateTime,
  InlineError,
  Labelled,
  Loading,
  Modal,
  statusPill,
  useAsync,
  useSubmitLatch,
} from "./ui.js";

/**
 * The customer's permanent identifier, derived from the company name.
 *
 * ⚠️ This MUST agree with `requireSlug` in services/gateway/src/routes/admin.ts.
 * It is a copy, because the console and the gateway are different packages and
 * neither can import the other — the same situation as env.ts and
 * apps/fe/src/env-config.js, solved the same way: copy the value, then have a
 * test read the other file and fail if the two ever differ
 * (`slug-rule.test.ts`). A silent divergence here would show a green field and
 * then a 400 on the one button that matters.
 */
export const SLUG_RULE = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;

export function slugify(companyName: string): string {
  return companyName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip accents: "École" → "ecole"
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50)
    .replace(/-+$/, ""); // a trailing hyphen after the cut is not a legal slug
}

export function slugProblem(slug: string): string | null {
  if (!slug) return "An identifier is required.";
  if (slug.length < 3) return "It must be at least 3 characters.";
  if (slug.length > 50) return "It must be 50 characters or fewer.";
  if (!SLUG_RULE.test(slug)) {
    return "Lowercase letters, numbers and hyphens only, and it cannot start or end with a hyphen.";
  }
  return null;
}

// ── The queue ───────────────────────────────────────────────────────────────

type Dialog =
  | { kind: "approve"; row: RegistrationRequest }
  | { kind: "reject"; row: RegistrationRequest }
  | null;

export function RequestsScreen({ api }: { api: ConsoleApi }) {
  const { state, retry } = useAsync<RequestList>(() => api.requests(), []);
  const [showDecided, setShowDecided] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  // What just happened, kept on screen after the dialog closes. A queue that
  // simply refreshes gives no confirmation that the thing you clicked worked.
  const [outcome, setOutcome] = useState<string | null>(null);

  const done = (message: string) => {
    setDialog(null);
    setOutcome(message);
    retry();
  };

  return (
    <div className="console-page">
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "var(--text-strong)" }}>
          Signup requests
        </h1>
        {state.t === "ok" && (
          <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            {state.data.pending} waiting · {state.data.total} in total
          </span>
        )}
        <div style={{ flex: 1 }} />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12.5,
            color: "var(--text-muted)",
            cursor: "default",
          }}
        >
          <input
            type="checkbox"
            className="focusable"
            checked={showDecided}
            onChange={(e) => setShowDecided(e.target.checked)}
          />
          Show ones I have already decided
        </label>
      </div>

      {outcome && (
        <div className="card" style={{ padding: "10px 12px", marginBottom: 14, maxWidth: 720 }}>
          <span className="pill accepted" style={{ marginRight: 8 }}>
            Done
          </span>
          <span style={{ fontSize: 12.5, color: "var(--text)" }}>{outcome}</span>
        </div>
      )}

      {state.t === "loading" && <Loading label="Reading the queue…" />}
      {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
      {state.t === "ok" && (
        <QueueTable
          list={state.data}
          showDecided={showDecided}
          onApprove={(row) => setDialog({ kind: "approve", row })}
          onReject={(row) => setDialog({ kind: "reject", row })}
        />
      )}

      {dialog?.kind === "approve" && (
        <ApproveDialog
          api={api}
          row={dialog.row}
          onClose={() => setDialog(null)}
          onDone={done}
        />
      )}
      {dialog?.kind === "reject" && (
        <RejectDialog api={api} row={dialog.row} onClose={() => setDialog(null)} onDone={done} />
      )}
    </div>
  );
}

function QueueTable({
  list,
  showDecided,
  onApprove,
  onReject,
}: {
  list: RequestList;
  showDecided: boolean;
  onApprove: (r: RegistrationRequest) => void;
  onReject: (r: RegistrationRequest) => void;
}) {
  const rows = showDecided
    ? list.requests
    : list.requests.filter((r) => r.status === "pending" || r.status === "approving");

  if (rows.length === 0) {
    return (
      <div className="card" style={{ padding: 24, maxWidth: 620 }}>
        <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>
          {list.total === 0 ? "No requests yet" : "Nothing waiting"}
        </p>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>
          {list.total === 0 ? (
            <>
              Nobody has asked for an account in this environment. The public request form is
              Stage B3 — until it exists, a request can only arrive from a direct call to the
              gateway, so an empty queue here is expected rather than a sign that something is
              broken.
            </>
          ) : (
            <>
              All {list.total} request{list.total === 1 ? " has" : "s have"} been decided. Tick{" "}
              <em>Show ones I have already decided</em> to see them.
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Who asked</th>
            <th>Worth a look</th>
            <th>Arrived</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <RequestRow key={r.id} r={r} onApprove={onApprove} onReject={onReject} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RequestRow({
  r,
  onApprove,
  onReject,
}: {
  r: RegistrationRequest;
  onApprove: (r: RegistrationRequest) => void;
  onReject: (r: RegistrationRequest) => void;
}) {
  const decidable = r.status === "pending" || r.status === "approving";
  return (
    // Tinted, not hidden. `data-flagged` drives a single left border in
    // console.css — enough to catch the eye while scanning, not enough to read
    // as a warning about the person.
    <tr data-flagged={r.flags.length > 0 ? "true" : undefined}>
      <td data-label="Company">
        <span style={{ fontWeight: 600, color: "var(--text-strong)" }}>{r.companyName}</span>
      </td>
      <td data-label="Who asked">
        <div>{r.contactName}</div>
        <div className="truncate" style={{ maxWidth: 220, fontSize: 12, color: "var(--text-muted)" }}>
          {r.contactEmail}
        </div>
      </td>
      <td data-label="Worth a look">
        {r.flags.length === 0 ? (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        ) : (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {r.flags.map((f) => (
              <span key={f} className="pill issues">
                {f}
              </span>
            ))}
          </span>
        )}
      </td>
      <td data-label="Arrived">{fmtDateTime(r.submittedAt)}</td>
      <td data-label="Status">
        <span className={statusPill(r.status)}>{r.status}</span>
        {/* `approving` means an earlier attempt stopped partway. Saying so is
            the difference between a stuck-looking row and a known state. */}
        {r.status === "approving" && (
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}>
            An earlier attempt did not finish. Approving again resumes it.
          </div>
        )}
        {r.decisionReason && r.status !== "pending" && r.status !== "approving" && (
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}>
            {r.decisionReason}
          </div>
        )}
      </td>
      <td data-label="">
        {decidable ? (
          <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
            <button className="btn primary sm focusable" onClick={() => onApprove(r)}>
              Approve &amp; invite
            </button>
            <button className="btn danger sm focusable" onClick={() => onReject(r)}>
              Reject
            </button>
          </span>
        ) : (
          <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{fmtDateTime(r.decidedAt)}</span>
        )}
      </td>
    </tr>
  );
}

// ── Approve ─────────────────────────────────────────────────────────────────

type ApprovePhase =
  | { t: "form" }
  | { t: "working" }
  | { t: "done"; result: ApproveResult }
  | { t: "failed"; err: ApiError; recovered: Recovered };

/**
 * What the server says exists NOW, read back after a failure.
 *
 * Never inferred from how far the request appeared to get. The provisioning
 * sequence writes the new workspace id onto the request row in its own
 * transaction BEFORE it calls Auth0, precisely so this question has a truthful
 * answer after a crash — so the honest thing is to ask it.
 */
type Recovered =
  | { t: "workspace-exists" }
  | { t: "nothing-created" }
  | { t: "unknown" };

function ApproveDialog({
  api,
  row,
  onClose,
  onDone,
}: {
  api: ConsoleApi;
  row: RegistrationRequest;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [displayName, setDisplayName] = useState(row.companyName);
  const [slug, setSlug] = useState(slugify(row.companyName));
  const [isInternal, setIsInternal] = useState(false);
  const [reason, setReason] = useState("");
  const [phase, setPhase] = useState<ApprovePhase>({ t: "form" });
  const { run, busy } = useSubmitLatch();

  const problem = slugProblem(slug);
  const reasonTooShort = reason.trim().length < 3;
  const blocked = Boolean(problem) || reasonTooShort || !displayName.trim();

  const submit = () => {
    if (blocked) return;
    setPhase({ t: "working" });
    run(
      () =>
        api.approveRequest(row.id, {
          slug,
          displayName: displayName.trim(),
          reason: reason.trim(),
          isInternal,
        }),
      (result) => setPhase({ t: "done", result }),
      (err) => {
        // ⚠️ Ask the server what survived before saying anything. The alternative
        // — guessing from the error text — is how an operator gets told "nothing
        // was created" about a workspace that exists, and creates a second one.
        setPhase({ t: "failed", err, recovered: { t: "unknown" } });
        void api
          .requests()
          .then((list) => {
            const fresh = list.requests.find((r) => r.id === row.id);
            const recovered: Recovered = !fresh
              ? { t: "unknown" }
              : fresh.provisionedOrgId
                ? { t: "workspace-exists" }
                : { t: "nothing-created" };
            setPhase((p) => (p.t === "failed" ? { ...p, recovered } : p));
          })
          .catch(() => {
            /* the error above is still the headline; leave `unknown` */
          });
      },
    );
  };

  const title =
    phase.t === "done" ? "Approved" : phase.t === "failed" ? "It did not finish" : "Approve & invite";

  return (
    <Modal
      title={title}
      busy={busy}
      wide
      onClose={onClose}
      footer={
        phase.t === "done" ? (
          <button
            className="btn primary focusable"
            onClick={() =>
              onDone(
                `${displayName.trim()} is set up. ${row.contactEmail} has been emailed a link to set a password.`,
              )
            }
          >
            Close
          </button>
        ) : phase.t === "failed" ? (
          <>
            <button className="btn ghost focusable" onClick={onClose}>
              Close
            </button>
            <button className="btn primary focusable" onClick={() => setPhase({ t: "form" })}>
              Back to the form
            </button>
          </>
        ) : (
          <>
            <button className="btn ghost focusable" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="btn primary focusable"
              onClick={submit}
              disabled={busy || blocked}
              data-testid="approve-submit"
            >
              {busy ? "Working…" : "Approve & invite"}
            </button>
          </>
        )
      }
    >
      {phase.t === "form" && (
        <ApproveForm
          row={row}
          displayName={displayName}
          setDisplayName={setDisplayName}
          slug={slug}
          setSlug={setSlug}
          slugIssue={problem}
          isInternal={isInternal}
          setIsInternal={setIsInternal}
          reason={reason}
          setReason={setReason}
          reasonTooShort={reasonTooShort}
        />
      )}
      {phase.t === "working" && <ApproveWorking email={row.contactEmail} />}
      {phase.t === "done" && <ApproveDone result={phase.result} email={row.contactEmail} slug={slug} />}
      {phase.t === "failed" && <ApproveFailed err={phase.err} recovered={phase.recovered} />}
    </Modal>
  );
}

function ApproveForm({
  row,
  displayName,
  setDisplayName,
  slug,
  setSlug,
  slugIssue,
  isInternal,
  setIsInternal,
  reason,
  setReason,
  reasonTooShort,
}: {
  row: RegistrationRequest;
  displayName: string;
  setDisplayName: (v: string) => void;
  slug: string;
  setSlug: (v: string) => void;
  slugIssue: string | null;
  isInternal: boolean;
  setIsInternal: (v: boolean) => void;
  reason: string;
  setReason: (v: string) => void;
  reasonTooShort: boolean;
}) {
  return (
    <>
      <div className="card" style={{ padding: "10px 12px", marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: "var(--text)" }}>
          <strong>{row.contactName}</strong> · {row.contactEmail}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
          {row.companyName} · arrived{" "}
          {fmtDateTime(row.submittedAt)}
        </div>
        {row.flags.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
            {row.flags.map((f) => (
              <span key={f} className="pill issues">
                {f}
              </span>
            ))}
          </div>
        )}
      </div>

      <Labelled label="Workspace name" hint="What they will see at the top of the app. Can be changed later.">
        <input
          className="field focusable"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          style={{ width: "100%" }}
        />
      </Labelled>

      <Labelled
        label="Identifier"
        hint={
          slugIssue ? (
            <span style={{ color: "var(--error-text)" }}>{slugIssue}</span>
          ) : (
            <>
              ⚠️ Permanent. This is how the customer is identified everywhere and it cannot be
              changed afterwards without creating a second record.
            </>
          )
        }
      >
        <input
          className="field focusable"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          style={{ width: "100%", fontFamily: "var(--font-mono)" }}
          data-testid="approve-slug"
        />
      </Labelled>

      <Labelled
        label="Why you are approving"
        hint={
          reasonTooShort ? (
            <span style={{ color: "var(--error-text)" }}>
              Required — it is recorded in the audit trail and cannot be edited later.
            </span>
          ) : (
            "Recorded in the audit trail. It is for you in six months, not for them."
          )
        }
      >
        <textarea
          className="field focusable"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          style={{ width: "100%" }}
          data-testid="approve-reason"
        />
      </Labelled>

      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 12.5,
          color: "var(--text-muted)",
          cursor: "default",
        }}
      >
        <input
          type="checkbox"
          className="focusable"
          checked={isInternal}
          onChange={(e) => setIsInternal(e.target.checked)}
        />
        This is a Dynamo test account — keep it out of the customer numbers
      </label>
    </>
  );
}

/**
 * ⚠️ NOT a progress bar.
 *
 * Approving is one HTTP call that runs four steps on the server, and this app
 * cannot observe them individually. A stepper that lit each line up on a timer
 * would be inventing knowledge it does not have — and on the one screen where
 * "the email has been sent" is a claim about the real world. So the steps are
 * listed as what is happening, all in the same neutral state, and the caption
 * says plainly that the console is waiting.
 */
function ApproveWorking({ email }: { email: string }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <svg className="spin" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="var(--border-strong)" strokeWidth="3" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <strong style={{ fontSize: 13, color: "var(--text-strong)" }}>Working…</strong>
      </div>
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: "var(--text)" }}>
        <li>Create the workspace and its first admin.</li>
        <li>Create or adopt their sign-in identity.</li>
        <li>Email {email} a link to set a password.</li>
      </ol>
      <p style={{ margin: "12px 0 0", fontSize: 12, lineHeight: 1.55, color: "var(--text-muted)" }}>
        These run on the server as one operation, so the console cannot show them ticking off one
        by one — it is waiting for the whole thing. Do not close this window; closing it would not
        stop anything, it would only stop you seeing the result.
      </p>
    </div>
  );
}

function ApproveDone({
  result,
  email,
  slug,
}: {
  result: ApproveResult;
  email: string;
  slug: string;
}) {
  if (result.alreadyApproved) {
    return (
      <div>
        <p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
          This request had already been approved, so <strong>nothing ran a second time</strong> —
          no new workspace, and no second email to {email}.
        </p>
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
          You are probably looking at a queue that was loaded before someone (or an earlier click)
          finished the job.
        </p>
      </div>
    );
  }
  return (
    <div>
      <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13, lineHeight: 1.8, color: "var(--text)" }}>
        <li>
          Workspace created — <code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{slug}</code>
        </li>
        <li>
          {result.auth0UserCreated
            ? "A new sign-in identity was created for them."
            : "They already had a Dynamo sign-in, so that identity was reused rather than duplicated."}
        </li>
        <li>{email} has been emailed a link to set a password.</li>
      </ul>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
        They cannot get in until they click that link. If it does not arrive, the customer page has
        everything you need to look it up — the email is sent by Auth0, not by this console.
      </p>
    </div>
  );
}

/**
 * The failure state, given the same care as the success state.
 *
 * Three things an operator needs and usually does not get: what went wrong, what
 * exists in the world right now, and whether pressing the button again is safe.
 */
function ApproveFailed({ err, recovered }: { err: ApiError; recovered: Recovered }) {
  return (
    <div>
      <InlineError err={err} />
      <p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
        {recovered.t === "workspace-exists" && (
          <>
            <strong>The workspace was created</strong> before this stopped, and it has been kept.
            The invitation email has not been sent.
          </>
        )}
        {recovered.t === "nothing-created" && (
          <>
            <strong>Nothing was created.</strong> There is no half-made customer to clean up.
          </>
        )}
        {recovered.t === "unknown" && (
          <>Checking what survived… if this does not resolve, reload the queue and look at the row.</>
        )}
      </p>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        <strong>Approving again is safe.</strong> The request has been put back in the queue, and a
        second attempt picks up where this one stopped — it will not create a second workspace and
        it will not send two emails.
      </p>
    </div>
  );
}

// ── Reject ──────────────────────────────────────────────────────────────────

/**
 * Preset reasons, so a decision six months old still reads as a decision rather
 * than as a mood. The free text is appended, never replaced.
 */
const REJECT_REASONS = [
  "Not a real company",
  "They are already a customer",
  "Not a fit for Dynamo right now",
  "Could not verify who they are",
  "Duplicate of another request",
  "Other",
] as const;

function RejectDialog({
  api,
  row,
  onClose,
  onDone,
}: {
  api: ConsoleApi;
  row: RegistrationRequest;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [preset, setPreset] = useState<string>(REJECT_REASONS[0]);
  const [notes, setNotes] = useState("");
  const [spam, setSpam] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const { run, busy } = useSubmitLatch();

  const recorded = preset === "Other" ? notes.trim() : [preset, notes.trim()].filter(Boolean).join(" — ");
  const blocked = recorded.length < 3;

  const submit = () => {
    if (blocked) return;
    setErr(null);
    run(
      () => api.rejectRequest(row.id, recorded, spam),
      () =>
        onDone(
          `${row.companyName} was ${spam ? "filed as spam" : "rejected"}. Nothing has been emailed to them.`,
        ),
      (e) => setErr(e),
    );
  };

  return (
    <Modal
      title={`Reject ${row.companyName}`}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost focusable" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn danger focusable"
            onClick={submit}
            disabled={busy || blocked}
            data-testid="reject-submit"
          >
            {busy ? "Working…" : spam ? "File as spam" : "Reject"}
          </button>
        </>
      }
    >
      {err && <InlineError err={err} />}

      <Labelled label="Reason">
        <select
          className="field select-elegant focusable"
          value={preset}
          onChange={(e) => setPreset(e.target.value)}
          style={{ width: "100%" }}
        >
          {REJECT_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Labelled>

      <Labelled
        label={preset === "Other" ? "What happened" : "Anything to add"}
        hint={
          blocked ? (
            <span style={{ color: "var(--error-text)" }}>
              A reason is required. It is recorded and cannot be edited later.
            </span>
          ) : null
        }
      >
        <textarea
          className="field focusable"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          style={{ width: "100%" }}
          data-testid="reject-notes"
        />
      </Labelled>

      {/* ★ What gets RECORDED, shown before it is recorded — not a preview of a
          message to the customer, because there is no message to the customer.
          See the notice below. */}
      <div className="card" style={{ padding: "10px 12px", marginBottom: 12 }}>
        <div
          style={{
            fontSize: 10.5,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--text-faint)",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          Recorded as
        </div>
        <div style={{ fontSize: 13, color: "var(--text)" }} data-testid="reject-preview">
          {recorded || <span style={{ color: "var(--text-faint)" }}>—</span>}
        </div>
      </div>

      {/* ⚠️ HONESTY, not decoration. Dynamo has no way to send a rejection email:
          the only message this system can send is Auth0's password-set link.
          Calling this box a "preview of what they will be told" — as the plan
          originally did — would describe an email that does not exist, and Omar
          would reasonably assume the person had heard back. */}
      <div
        className="card"
        style={{ padding: "10px 12px", marginBottom: 12 }}
        data-testid="reject-no-email-notice"
      >
        <span className="pill issues" style={{ marginRight: 8 }}>
          Nothing is sent
        </span>
        <span style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
          Rejecting records this reason for you. It does <strong>not</strong> email them — Dynamo
          has no way to send that message today. If you want them to hear back, write to them
          yourself.
        </span>
      </div>

      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 12.5,
          color: "var(--text-muted)",
          cursor: "default",
        }}
      >
        <input
          type="checkbox"
          className="focusable"
          checked={spam}
          onChange={(e) => setSpam(e.target.checked)}
        />
        This is junk — file it as spam rather than as a decision
      </label>
    </Modal>
  );
}
